import { useClient } from '@/context/ClientContext'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  DownloadJobStatus,
  GetDownloadJobResponse,
} from '@droidmap/asset-service-contract'
import { DroidMapMetadata } from '@droidmap/shared-service-contract'

export type DownloadProgress = {
  percent: number
  processedAssets: number
  totalAssets: number
  processedBytes: number
  totalBytes: number
}

export type UseAssetSetDownloadResult = {
  initiateDownload: (assetSetId: string) => void
  isInitiating: boolean
  downloadJob: GetDownloadJobResponse | null
  isPolling: boolean
  isDownloading: boolean
  progress: DownloadProgress | null
  failedAssets: number
  error: Error | null
  reset: () => void
}

export function useAssetSetDownload(
  metadata?: DroidMapMetadata,
): UseAssetSetDownloadResult {
  const { assetClient } = useClient()
  const queryClient = useQueryClient()
  const [downloadJobId, setDownloadJobId] = useState<string | null>(null)
  // A guard, not state: it is never rendered, and only exists so the auto-download
  // navigation fires once per job. As state it forced an extra render on every
  // completed job for a value nothing reads.
  const hasAutoDownloadedRef = useRef(false)

  // Mutation to initiate download
  const initiateMutation = useMutation({
    mutationFn: async (assetSetId: string) => {
      const job = await assetClient.initiateAssetSetDownload(
        { assetSetId },
        metadata,
      )
      return job
    },
    onSuccess: (job) => {
      setDownloadJobId(job.id)
      hasAutoDownloadedRef.current = false
    },
  })

  // Query to poll download status
  const statusQuery = useQuery({
    queryKey: ['downloadJob', downloadJobId],
    queryFn: async () => {
      if (!downloadJobId) return null
      return await assetClient.getDownloadJob({ downloadJobId }, metadata)
    },
    enabled: !!downloadJobId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return 5000
      if (
        data.status === DownloadJobStatus.Completed ||
        data.status === DownloadJobStatus.Failed
      ) {
        return false
      }
      return 5000
    },
  })

  // Auto-download when ready
  useEffect(() => {
    if (
      statusQuery.data?.status === DownloadJobStatus.Completed &&
      statusQuery.data.downloadUrl &&
      !hasAutoDownloadedRef.current
    ) {
      hasAutoDownloadedRef.current = true

      // Trigger download using window.location for cross-origin URLs
      // The S3 presigned URL has Content-Disposition: attachment set, so it will download
      window.location.href = statusQuery.data.downloadUrl
    }
  }, [statusQuery.data])

  // Derived, not stored: a failed job *is* the error, so there is nothing to
  // keep in sync. Storing it in an effect meant one render showed a failed job
  // with `error: null` before the effect re-rendered with the message.
  const failureError =
    statusQuery.data?.status === DownloadJobStatus.Failed
      ? new Error(
          statusQuery.data.errorMessage || 'Download failed. Please try again.',
        )
      : null

  // Calculate progress
  const progress: DownloadProgress | null =
    statusQuery.data && statusQuery.data.totalAssets
      ? (() => {
          const processedBytes = statusQuery.data.processedBytes || 0
          const totalBytes = statusQuery.data.totalBytes || 0
          const percent =
            totalBytes > 0 ? Math.round((processedBytes / totalBytes) * 100) : 0
          return {
            percent,
            processedAssets: statusQuery.data.processedAssets,
            totalAssets: statusQuery.data.totalAssets,
            processedBytes,
            totalBytes,
          }
        })()
      : null

  // Reset function to clear state
  const reset = useCallback(() => {
    setDownloadJobId(null)
    hasAutoDownloadedRef.current = false
    queryClient.removeQueries({ queryKey: ['downloadJob'] })
  }, [queryClient])

  // Initiate download function
  const initiateDownload = useCallback(
    (assetSetId: string) => {
      reset()
      initiateMutation.mutate(assetSetId)
    },
    [initiateMutation, reset],
  )

  // isDownloading is true when we have an active job that's not completed/failed
  const isDownloading =
    initiateMutation.isPending ||
    (downloadJobId !== null &&
      statusQuery.data?.status !== DownloadJobStatus.Completed &&
      statusQuery.data?.status !== DownloadJobStatus.Failed)

  return {
    initiateDownload,
    isInitiating: initiateMutation.isPending,
    downloadJob: statusQuery.data ?? null,
    isPolling: statusQuery.isFetching && downloadJobId !== null,
    isDownloading,
    progress,
    failedAssets: statusQuery.data?.failedAssets ?? 0,
    error: (initiateMutation.error as Error | null) || failureError,
    reset,
  }
}
